/**
 * /changelog -- in-app release notes.
 *
 * Pulls /changelog.json (shipped as a static asset) and renders one section
 * per release with kind-tagged highlight cards. On mount, persists the
 * latest version to localStorage so the topbar "What's new" dot disappears.
 */
import { useEffect, useState } from 'react';
import { Sparkles, ShieldCheck, Wrench, FileText, Lock, ScrollText } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface Highlight {
  kind: 'feature' | 'security' | 'compliance' | 'fix' | 'polish' | 'docs';
  title: string;
  body: string;
}

interface Release {
  version: string;
  date: string;
  title: string;
  highlights: Highlight[];
}

interface ChangelogJson {
  releases: Release[];
}

export const CHANGELOG_LAST_SEEN_KEY = 'praxara:lastSeenChangelogVersion';

const KIND_META: Record<Highlight['kind'], { label: string; tint: string; Icon: typeof Sparkles }> = {
  feature: { label: 'New', tint: 'bg-primary/10 text-primary border-primary/20', Icon: Sparkles },
  security: { label: 'Security', tint: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900', Icon: Lock },
  compliance: { label: 'Compliance', tint: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900', Icon: ShieldCheck },
  fix: { label: 'Fix', tint: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900', Icon: Wrench },
  polish: { label: 'Polish', tint: 'bg-muted text-muted-foreground border-muted', Icon: ScrollText },
  docs: { label: 'Docs', tint: 'bg-muted text-muted-foreground border-muted', Icon: FileText },
};

export function ChangelogPage() {
  const [data, setData] = useState<ChangelogJson | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/changelog.json', { cache: 'no-store' })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return (await r.json()) as ChangelogJson;
      })
      .then((j) => {
        if (cancelled) return;
        setData(j);
        const latest = j.releases[0]?.version;
        if (latest) {
          try {
            localStorage.setItem(CHANGELOG_LAST_SEEN_KEY, latest);
          } catch {
            // ignore storage errors (Safari private mode, etc.)
          }
        }
      })
      .catch((err) => !cancelled && setError(String(err)));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="mx-auto w-full max-w-3xl space-y-8">
      <header className="space-y-2">
        <div className="inline-flex items-center gap-2 rounded-full border bg-muted/30 px-3 py-1 text-xs uppercase tracking-wider text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5" /> What's new
        </div>
        <h1 className="font-display text-3xl">Praxara changelog</h1>
        <p className="text-muted-foreground">
          Customer-facing release notes. For every release we publish: what shipped, why it matters, and where to find it. Compliance- or security-tagged items are linked from the Trust Center.
        </p>
      </header>

      {error && (
        <Card>
          <CardContent className="p-6 text-sm text-destructive">
            Could not load changelog: {error}
          </CardContent>
        </Card>
      )}

      {!data && !error && (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Card key={i}>
              <CardContent className="space-y-3 p-6">
                <div className="h-4 w-32 animate-pulse rounded bg-muted" />
                <div className="h-6 w-2/3 animate-pulse rounded bg-muted" />
                <div className="h-3 w-full animate-pulse rounded bg-muted" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {data && data.releases.map((rel) => (
        <ReleaseSection key={rel.version} release={rel} />
      ))}
    </div>
  );
}

function ReleaseSection({ release }: { release: Release }) {
  const dateLabel = new Date(release.date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-baseline gap-3 border-b pb-2">
        <Badge variant="outline" className="font-mono text-[11px]">v{release.version}</Badge>
        <span className="text-sm text-muted-foreground">{dateLabel}</span>
        <h2 className="font-display text-xl">{release.title}</h2>
      </div>
      <div className="grid gap-3">
        {release.highlights.map((h, i) => (
          <HighlightCard key={i} highlight={h} />
        ))}
      </div>
    </section>
  );
}

function HighlightCard({ highlight }: { highlight: Highlight }) {
  const meta = KIND_META[highlight.kind] ?? KIND_META.feature;
  const Icon = meta.Icon;
  return (
    <Card>
      <CardContent className="space-y-2 p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-medium text-base">{highlight.title}</h3>
          <span
            className={cn(
              'inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider',
              meta.tint,
            )}
          >
            <Icon className="h-3 w-3" />
            {meta.label}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">{highlight.body}</p>
      </CardContent>
    </Card>
  );
}
